import { z } from "zod";
import { fail, foldText, ok, type FieldKind } from "../kind";

export const checkboxSettings = z.object({
  display: z.enum(["checkbox", "toggle"]).default("checkbox"),
  // Texte à côté de la case (« Payé »), facultatif.
  label: z.string().max(60).optional(),
});

const TRUE_WORDS = new Set(["true", "oui", "yes", "y", "o", "1", "x", "✓", "✔", "vrai", "checked", "coche", "on"]);
const FALSE_WORDS = new Set(["false", "non", "no", "n", "0", "faux", "unchecked", "off", "-"]);

export const checkboxKind: FieldKind<"checkbox"> = {
  type: "checkbox",
  settings: checkboxSettings,
  value: z.boolean(),
  coerce(input) {
    if (typeof input === "boolean") return ok(input);
    if (typeof input === "number") {
      if (input === 1) return ok(true);
      if (input === 0) return ok(false);
      return fail("Expected a boolean.");
    }
    if (typeof input === "string") {
      const word = foldText(input.trim());
      if (TRUE_WORDS.has(word)) return ok(true);
      if (FALSE_WORDS.has(word)) return ok(false);
      return fail(`"${input}" is neither yes nor no.`);
    }
    return fail("Expected a boolean.");
  },
  // Décochée = « non », c'est une réponse ; seul `null` est vide.
  format: (value, _settings, ctx) =>
    ctx.style === "plain" ? String(value) : value ? "✓" : "",
  compare: (a, b) => Number(a) - Number(b),
  filters: [
    { id: "is_checked", operand: "none", test: (v) => v },
    { id: "is_unchecked", operand: "none", matchesEmpty: true, test: (v) => !v },
  ],
  aggregates: ["count", "count_checked", "count_unchecked", "percent_checked", "percent_unchecked"],
  groupKeys: (value) => [String(value)],
  emptyGroupKey: "false",
  groups: () => [
    { key: "true", label: "✓" },
    { key: "false", label: "—" },
  ],
};
